/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {AxiosError, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse } from 'axios';
import ApiException from '@/exceptions/ApiException';
import {AbstractHttpService} from '@/services/http/abstract';

/**
 * Api service class used to leverage http library in order to communicate with api.
 *
 * @author Noel Chapman <nchapman@frontier.co.uk>
 */
export class CmsService extends AbstractHttpService {
    /**
     *  Constructor.
     *
     *  This includes the configuration of the following plugins:
     *  1. Throttle adapter - ensures that a route is not flooded with requests during a pre-defined time period.
     *  2. Cache adapter - caches requests. Important - this should be deactivated on some routes: {cache: false}.
     *  3. Retry plugin - attempts retries on routes that fail.
     */
    constructor() {
        super({
            accept: 'application/json',
            contentType: 'application/json',
            cache: false,
            baseURL: process.env.VUE_APP_CMS_API || 'https://cms.zaonce.net',
            throttle: {
                threshold: 5000,
            },
            params: {
                _format: 'json',
            },
            retry: {
                period: 500,
                maximum: 5,
                condition: (error: AxiosError): boolean => (
                    !error.response ||
                    error.response.status === 429 ||
                    (error.response.status >= 500 && error.response.status <= 599)
                ),
            },
        });
    }

    /**
     * Add request interceptors.
     *
     * This method can be overridden in inheriting classes - as can the default request interceptors that it registers.
     *
     * @param {AxiosInterceptorManager<AxiosRequestConfig>} request
     */
    public addRequestInterceptors(request: AxiosInterceptorManager<AxiosRequestConfig>): void {
        super.addRequestInterceptors(request);
    }

    /**
     * Global success response handler
     *
     * @override
     *
     * @param {AxiosResponse} response
     * @returns {AxiosResponse}
     */
    public handleSuccess(response: AxiosResponse): AxiosResponse {
        // Custom response handling

        return response;
    }

    /**
     * Inheritable exception handler.
     *
     * @override
     *
     * @param {ApiException} exception
     * @return {Promise<ApiException>}
     */
    public handleException(exception: ApiException): Promise<never> {
        // Custom error handling

        return Promise.reject(exception);
    }
}
